import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";

const HERO = "https://customer-assets-agu9un31.emergentagent.net/job_luxury-transport-hub-5/artifacts/nnm38h6p_downtown%20with%20SUV.png";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const emailValid = EMAIL_RE.test(email.trim());
  const showEmailError = email.length > 0 && !emailValid;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!emailValid) return;
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "bg-[#1a1a1d] border-white/15 text-white focus-visible:ring-[#D4AF37] rounded-sm h-12";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background" data-testid="login-page">
      <div className="relative hidden lg:block">
        <img src={HERO} alt="Luxury limousine" className="w-full h-full object-cover" style={{ filter: "brightness(1.2) contrast(1.04) saturate(1.08)" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
        <div className="absolute bottom-16 left-12 right-12" style={{ textShadow: "0 2px 8px rgba(0,0,0,0.9)" }}>
          <Link to="/" className="inline-block"><Logo imgClass="h-12 w-12" textClass="text-3xl" /></Link>
          <h2 className="font-heading text-4xl font-light mt-6 leading-tight">Your chauffeur awaits.</h2>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex justify-center mb-10"><Logo imgClass="h-12 w-12" textClass="text-3xl" /></Link>
          <p className="text-xs tracking-label uppercase gold-text mb-3">Welcome Back</p>
          <h1 className="font-heading text-4xl font-light tracking-tight">Sign in</h1>

          <div className="mt-5">
            <p className="text-lg text-white font-light">New to Aspen Executive Limo?</p>
            <div className="mt-3 flex flex-wrap gap-3">
              <Link
                to="/register"
                data-testid="goto-register"
                className="inline-flex items-center justify-center bg-[#D4AF37] text-black hover:bg-[#E0BC4A] transition-all duration-300 rounded-sm px-[3px] py-[1px] font-bold tracking-wide"
              >
                Create an account
              </Link>
              <Link
                to="/"
                onClick={() => window.scrollTo({ top: 0, left: 0, behavior: "auto" })}
                data-testid="continue-as-guest-btn"
                className="inline-flex items-center justify-center bg-[#D4AF37] text-black hover:bg-[#E0BC4A] transition-all duration-300 rounded-sm px-[3px] py-[1px] font-bold tracking-wide"
              >
                Continue as Guest
              </Link>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="mt-10 space-y-5">
            <div>
              <Label className="text-zinc-400 text-xs tracking-wide mb-1.5 block">Email</Label>
              <Input data-testid="login-email" type="email" required
                className={`${inputCls} ${showEmailError ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                placeholder="you@example.com"
                value={email} onChange={(e) => setEmail(e.target.value)} />
              {showEmailError && (
                <p data-testid="login-email-error" className="text-red-400 text-sm mt-1.5 font-medium">
                  Please enter a valid email address.
                </p>
              )}
            </div>
            <div>
              <Label className="text-zinc-400 text-xs tracking-wide mb-1.5 block">Password</Label>
              <Input data-testid="login-password" type={showPassword ? "text" : "password"} required className={inputCls} placeholder="••••••••"
                value={password} onChange={(e) => setPassword(e.target.value)} />
              <label className="flex items-center gap-2 mt-2 w-fit cursor-pointer select-none">
                <input type="checkbox" data-testid="login-show-password" checked={showPassword} onChange={(e) => setShowPassword(e.target.checked)} className="h-4 w-4 accent-[#D4AF37] cursor-pointer" />
                <span className="text-zinc-400 text-sm">Show password</span>
              </label>
            </div>
            <button
              type="submit"
              disabled={loading || !emailValid}
              data-testid="login-submit-btn"
              className="flex items-center justify-center w-fit bg-[#D4AF37] text-black hover:bg-[#E0BC4A] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 rounded-sm px-[3px] py-[1px] font-medium tracking-wide"
            >
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
